import { defineStore } from 'pinia';
import { Config } from '@/config';
import apiService from '@/services/api.service';
import type { FeedbackEntryDto } from '@/services/types/api.types';

export interface FeedbackEntriesState {
  feedbackEntries: FeedbackEntryDto[];
  itemLoadingStates: Set<number>;
}

export const useFeedbackEntriesStore = defineStore({
  id: 'feedbackEntries',
  state: (): FeedbackEntriesState => ({
    feedbackEntries: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (projectModuleId: number) => state.itemLoadingStates.has(projectModuleId),
    feedbackEntryList: (state) => state.feedbackEntries,
  },
  actions: {
    async fetchFeedbackEntriesByModuleId(projectModuleId: number) {
      this.itemLoadingStates.add(projectModuleId);
      try {
        const { data } = await apiService.get<FeedbackEntryDto[]>(
          `${Config.api.v2}/project-modules/${projectModuleId}/feedback-entries`,
        );
        this.feedbackEntries = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(projectModuleId);
      }
    },
    async deleteFeedbackEntry(feedbackEntry: FeedbackEntryDto) {
      try {
        await apiService.delete(`${Config.api.v2}/feedback-entries/${feedbackEntry.id!}`);
        await this.fetchFeedbackEntriesByModuleId(feedbackEntry.project_module_id);
      } catch (error) {
        console.error(error);
      }
    },
    async approveFeedbackEntry(feedbackEntry: FeedbackEntryDto) {
      try {
        await apiService.post(`${Config.api.v2}/feedback-entries/${feedbackEntry.id!}/:approve`);
        await this.fetchFeedbackEntriesByModuleId(feedbackEntry.project_module_id);
      } catch (error) {
        console.error(error);
      }
    },
    async refuseFeedbackEntry(feedbackEntry: FeedbackEntryDto) {
      try {
        await apiService.post(`${Config.api.v2}/feedback-entries/${feedbackEntry.id!}/:refuse`);
        await this.fetchFeedbackEntriesByModuleId(feedbackEntry.project_module_id);
      } catch (error) {
        console.error(error);
      }
    },
  },
});
